import {
  ApplicationStatus, ApplicationStatusWithSubStatus,
} from '@/apis/family_application/requests'
import Badge, { ColorType } from '@/common/components/Badge.tsx'
import { useTranslation } from 'react-i18next'

export type ApplicationStatusBadgeProps = {
  status: ApplicationStatus
  subStatus: ApplicationStatusWithSubStatus | null
  className?: string
}

function assertNever(x: never): never {
  throw new Error(`Unhandled value: ${x}`)
}

export default function StudentStatusBadge({
  status,
  subStatus,
  className,
}: ApplicationStatusBadgeProps) {
  const { t: translate } = useTranslation()

  function getColor(status: ApplicationStatus) {
    let color: ColorType
    switch (status) {
      case 'Invited':
      case 'V1 Invited':
        color = 'Informative'
        break
      case 'Appeal':
      case 'Pending':
      case 'In Progress':
        color = 'Notice'
        break
      case 'Complete':
      case 'SoftPoint Renewal':
      case 'Funded':
      case 'ClassWallet Renewal':
      case 'Awarded':
      case 'Eligible':
        color = 'Positive'
        break

      case 'In Review':
      case 'Wait Listed':
      case 'Ineligible':
      case 'Age Phase Out':
      case 'Withdrawn':
        color = 'Negative'
        break
      default:
        return assertNever(status)
    }

    return color
  }

  function toTitleCase(str: string): string {
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  if (status === 'Eligible') {
    return <Badge semanticColor="Positive">{subStatus}</Badge>
  }

  if (status === 'Ineligible') {
    return <Badge semanticColor="Negative">{subStatus}</Badge>
  }

  const translatedStatus = translate(status.toLowerCase().replace(/ /g, '_'))
  const formattedStatus = toTitleCase(translatedStatus)

  return (
    <Badge semanticColor={getColor(status)} className={className}>
      {formattedStatus}
    </Badge>
  )
}
